import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import RefreshIcon from '@material-ui/icons/Refresh';
import { AppBarTitle } from '@components/app-bar-title';
import { IconButton } from '@material-ui/core';
import { RiSearchFill } from 'react-icons/ri';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import { UploadHelpSection } from './upload-help-section';
import Alert from '@material-ui/lab/Alert/Alert';
import {
  getExecutionStatus,
  getFiles,
  getS3Modules,
  getUploadUrl,
  uploadToS3,
} from '@api/s3-manager';
import { useSnackbar } from 'notistack';
import { FileTable } from './file-table';
import { getAlertSeverity, getExecutionStatusError, getStatusMessage, isValidFileName } from './utils';

export interface FileItem {
  id: number;
  file_name: string;
  size: string;
  last_modified: string;
  status?: string;
}

export default function S3FileUploadManager() {
  const [selectedFolder, setSelectedFolder] = useState('');
  const [modules, setModules] = useState<{ label: string; module: string }[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingModules, setLoadingModules] = useState(true);
  const [inputFiles, setInputFiles] = useState<FileItem[]>([]);
  const [outputFiles, setOutputFiles] = useState<FileItem[]>([]);
  const [errorFiles, setErrorFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [executionStatus, setExecutionStatus] = useState<string>('');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = useState(15);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const MAX_POLL_COUNT = 9;
  const [hasUploadedFile, setHasUploadedFile] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

 const EXCLUDED_MODULES = new Set([
    'content-mapping',
    'cast_n_crew_import_omdb',
  ]);

  const loadModules = () => {
    setLoadingModules(true);

    getS3Modules().subscribe({
      next: response => {
        const moduleList = (response?.modules || []).filter(
          ({ module }) => !EXCLUDED_MODULES.has(module)
        );

        setModules(moduleList);
        setLoadingModules(false);
      },
      error: error => {
        console.error('Failed to load modules', error);
        setLoadingModules(false);
      },
    });
  };

  const loadFiles = (moduleName: string) => {
    getFiles(moduleName).subscribe({
      next: response => {
        setInputFiles(response?.files?.input || []);
        setOutputFiles(response?.files?.output || []);
        setErrorFiles(response?.files?.error || []);
      },
    });
  };

  useEffect(()=>{
    if (selectedFolder)
       loadFiles(selectedFolder);
  },[selectedFolder])

  /*   const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.length) {
      setSelectedFile(event.target.files[0] || null);
    }
  }; */

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!isValidFileName(file.name)) {
      enqueueSnackbar(
        'File name contains unsupported special characters. Please rename the file using only letters, numbers, spaces, _, -, and .',
        { variant: 'warning' },
      );

      event.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFolder || !selectedFile) {
      return;
    }

    try {
      setUploading(true);

      setStatusMessage('Preparing upload...');
      const sanitizedFileName = selectedFile.name.replace(/[^a-zA-Z0-9 _\-\.!*'()]/g, '_');
      const signedUrlResponse = await getUploadUrl(selectedFolder, sanitizedFileName).toPromise();
      const signedUrl = signedUrlResponse?.signed_url;

      setStatusMessage('Uploading file to S3...');

      await uploadToS3(signedUrl, selectedFile)
        .then(response => {
          console.log('S3 Upload Success:', response.statusText);
        })
        .catch(error => {
          console.error('S3 Upload Error:', error);
          throw error;
        });
        setExecutionStatus('uploading');

        pollCountRef.current = 0;

        setTimeout(() => {
          startPolling();
        }, 15000);
      setStatusMessage('File uploaded successfully. Processing has started.');
        setHasUploadedFile(true);
      await loadFiles(selectedFolder);

      enqueueSnackbar('File uploaded successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);

      const message = error?.response?.data?.message || error?.message || 'Upload failed';

      enqueueSnackbar(`Upload failed. ${message}`, {
        variant: 'error',
      });

      setStatusMessage(message);
    } finally {
      setUploading(false);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const checkExecutionStatus = async (): Promise<boolean> => {
    try {
      const response = await getExecutionStatus(selectedFolder).toPromise();
      //console.log('Execution Status Response:', response);
      const status = response?.status ?? '';
      const backendMessage = response?.message ?? '';
      if (status) {
        setExecutionStatus(status);
         // Stop if processing completed
        if (
          status.toLowerCase() === 'success' ||
          status.toLowerCase() === 'failed'
        ) {
          await loadFiles(selectedFolder);
          return true;
        }
         // Stop if backend reports no active task
        if (
          status.toLowerCase() === 'idle' &&
          backendMessage.toLowerCase().includes('no active')
        ) {
          setExecutionStatus('timeout');
          return true;
        }
      }

      return false;
     /*  if (status.toLowerCase() === 'success' || status.toLowerCase() === 'failed') {
        stopPolling();
        setExecutionStatus(status);
        await loadFiles(selectedFolder);
      } */
    } catch (err) {
      console.error(err);
      setCheckingStatus(false);
      setExecutionStatus(getExecutionStatusError(err));

          enqueueSnackbar(getExecutionStatusError(err), {
            variant: 'error',
          });

    return true; // Stop polling on API error
    }
  };

  const startPolling = () => {
    stopPolling();
    pollCountRef.current = 0;
    setCheckingStatus(true);

    startCountdown();

    pollingRef.current = setInterval(async () => {
      pollCountRef.current++;

      //console.log(`Execution Status Poll: ${pollCountRef.current}/${MAX_POLL_COUNT}`);
    const shouldStop = await checkExecutionStatus();

      if (shouldStop) {
        stopPolling();
        return;
      }

      if (pollCountRef.current >= MAX_POLL_COUNT) {
        stopPolling();
        setExecutionStatus('timeout');
      }
    }, 15000);
  };

  const stopPolling = () => {
    setCheckingStatus(false);

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const startCountdown = () => {
    setCountdown(15);

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 15;
        }

        return prev - 1;
      });
    }, 1000);
  };

  const handleRefresh = () => {
    if (!selectedFolder) {
      enqueueSnackbar('Please select a folder first', { variant: 'warning' });
      return;
    }
    loadFiles(selectedFolder);
  };

  return (
    <>
      <AppBarTitle>
        <IconButton color="inherit">
          <RiSearchFill />
        </IconButton>{' '}
        S3 File Upload Manager
      </AppBarTitle>
      <Box p={4}>
        <Typography color="secondary" style={{ marginBottom: 4 }}>
          Upload files to S3 Input folders. Lambda will process files and move them to Output or
          Error folders.
        </Typography>

        <Grid container spacing={2}>
          {/* Step 1 */}
          <Grid item xs={12} md={3}>
            <Paper
              style={{
                padding: 24,
                height: '100%',
                textAlign: 'center',
              }}>
              <FolderOpenIcon color="primary" style={{ fontSize: 50 }} />

              <Typography variant="h6" style={{ marginTop: 12, marginBottom: 20 }}>
                Select Folder
              </Typography>

              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={selectedFolder}
                  disabled={loadingModules}
                  onChange={e => {
                   stopPolling();
                    const module = e.target.value as string;
                    setSelectedFolder(module);
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  renderValue={selected => {
                    if (loadingModules) {
                      return 'Loading folders...';
                    }

                    if (!selected) {
                      return 'Select Folder';
                    }

                    const selectedModule = modules.find(item => item.module === selected);

                    return selectedModule?.label || 'Select Folder';
                  }}>
                  <MenuItem value="" disabled>
                    Select Folder
                  </MenuItem>
                  {modules.map(item => (
                    <MenuItem key={item.module} value={item.module}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                {loadingModules && (
                  <Box
                    position="absolute"
                    right={35}
                    top="50%"
                    style={{ transform: 'translateY(-50%)' }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
              </FormControl>
            </Paper>
          </Grid>

          {/* Step 2 */}
          <Grid item xs={12} md={6}>
            <Paper
              style={{
                padding: 24,
                textAlign: 'center',
                borderRadius: 12,
              }}>
              <CloudUploadIcon
                color="primary"
                style={{
                  fontSize: 56,
                  margin: 8,
                }}
              />

              <Typography variant="h5" gutterBottom>
                Upload Import File
              </Typography>

              <Typography color="textSecondary" style={{ marginBottom: 24 }}>
                Select an Excel file and upload it to the selected S3 input folder.
              </Typography>
              {/* Status Message */}
              {uploading && (
                <Alert severity="info" style={{ margin: 10 }}>
                  {statusMessage}
                </Alert>
              )}
              <input
                ref={fileInputRef}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="upload-excel-file"
                type="file"
                onChange={handleFileChange}
              />

              <label htmlFor="upload-excel-file">
                <Button
                  component="span"
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<CloudUploadIcon />}>
                  Browse File
                </Button>
              </label>

              {selectedFile && (
                <Paper
                  elevation={0}
                  style={{
                    marginTop: 20,
                    padding: 12,
                    background: '#f5f7fa',
                  }}>
                  <Typography variant="body2">📄 {selectedFile.name}</Typography>
                </Paper>
              )}

              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginTop: 16 }}>
                Supported formats: .xlsx, .xls
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={!selectedFile || !selectedFolder || uploading}
                onClick={() => setConfirmOpen(true)}
                style={{
                  marginTop: 16,
                  minWidth: 180,
                }}>
                {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </Paper>
          </Grid>

          {/* Step 3 */}
          <Grid item xs={12} md={3}>
            <UploadHelpSection />
          </Grid>
         { <Grid item xs={12}>
            {executionStatus && (
              <Paper
                elevation={0}
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: '#f8fafc',
                  border: '1px solid #e0e0e0',
                }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Processing Status</Typography>

                  {!checkingStatus && (
                     <Tooltip title="Refresh processing status" arrow>
                      <IconButton
                        onClick={checkExecutionStatus}
                        size="small"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Alert severity={getAlertSeverity(executionStatus)}>
                  <Typography>{getStatusMessage(executionStatus,hasUploadedFile)}</Typography>

                  {checkingStatus && (
                    <Typography variant="caption">
                      Checking again in {countdown} seconds...
                    </Typography>
                  )}
                </Alert>
              </Paper>
            )}
          </Grid>}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">File Management</Typography>

              <Tooltip title={!selectedFolder ? 'Select a folder first' : 'Refresh all file lists'}>
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={!selectedFolder}>
                    Refresh All
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>

          {/* Bottom Panels */}
          <Grid item xs={12}>
            <FileTable
              title="Input Files"
              files={inputFiles}
              selectedFolder={selectedFolder}
              folder="input"
            />
          </Grid>

          <Grid item xs={12}>
            <FileTable
              title="Error Files"
              files={errorFiles}
              selectedFolder={selectedFolder}
              folder="error"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FileTable
              title="Output Files"
              files={outputFiles}
              selectedFolder={selectedFolder}
              folder="output"
            />
          </Grid>


          <Grid item xs={12}>
            <Paper
              style={{
                padding: 2,
                backgroundColor: '#f8fafc',
              }}>
              <Alert severity="info">
                Files uploaded to the selected input folder are processed automatically by AWS
                Lambda. Refresh the status to view the latest results.
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Upload</DialogTitle>

        <DialogContent>
          <Typography gutterBottom>Are you sure you want to upload the following file?</Typography>

          <Paper
            elevation={0}
            style={{
              padding: 12,
              marginTop: 12,
              background: '#f5f7fa',
            }}>
            <Typography>
              <strong>File:</strong> {selectedFile?.name}
            </Typography>

            <Typography>
              <strong>Folder:</strong> {modules.find(m => m.module === selectedFolder)?.label}
            </Typography>
          </Paper>

          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Once uploaded, the file will be sent to the selected S3 input folder and AWS Lambda will
            begin processing it automatically.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setSelectedFile(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}>
            No
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              setConfirmOpen(false);
              await handleUpload();
            }}>
            Yes, Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
